import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import axios from "axios";

import emailjs from "emailjs-com";
import { toast } from "react-toastify";
import { Autocomplete } from "@react-google-maps/api";
import StarRating from "../StarRating/StarRating";
import { motion } from "framer-motion";
import ResponsiveButton from "../../components/ResponsiveButton/ResponsiveButton";
import RecaptchaField from "../../components/RecaptchaField/RecaptchaField";
import { useLocation } from "react-router-dom"; // lisää ylös muiden importtien joukkoon

// EmailJS-ympäristömuuttujat (varmistathan, että ne ovat asetettu .env-tiedostossa)
const EMAILJS_USER_ID = process.env.REACT_APP_EMAILJS_USER_ID;
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID_PERFORMER;
const EMAILJS_TEMPLATE_ID_CUSTOMER = process.env.REACT_APP_EMAILJS_TEMPLATE_ID_CUSTOMER_PERFORMER;

const BookingForm = ({ bandSlug, selectedDate, formMode: propFormMode, isPremium = true }) => {
  const location = useLocation();
  const initialFormMode = propFormMode || location.state?.formMode || "setit";
  const [formMode] = useState(initialFormMode);

  // Lomakkeen kentät
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    additional_info: "",
    gig_length: ""
  });
  const [starRating, setStarRating] = useState(0);
  const [availableSets, setAvailableSets] = useState([]);
  const [bandRegions, setBandRegions] = useState([]);
  const [tapahtumapaikka, setTapahtumapaikka] = useState("");
  const [priceRegion, setPriceRegion] = useState("");
  const [price, setPrice] = useState(null);
  const [bandData, setBandData] = useState(null);

  const [recaptchaValue, setRecaptchaValue] = useState(null);
const recaptchaRef = useRef();

  // Tilat etäisyystiedoille
  // eslint-disable-next-line
  const [distanceText, setDistanceText] = useState("");
  // eslint-disable-next-line
  const [durationText, setDurationText] = useState("");
  // eslint-disable-next-line
  const [travelCost, setTravelCost] = useState(null);
  const [formError, setFormError] = useState(null);
  const autocompleteRef = useRef(null);
  
  // Funktio etäisyyden ja matka-ajan laskemiseen Google Maps Distance Matrix -palvelulla
  const getDistanceAndDuration = (origin, destination) => {
    return new Promise((resolve, reject) => {
      if (!window.google) {
        return reject(new Error("Google Maps API ei ole ladattu"));
      }
      const service = new window.google.maps.DistanceMatrixService();
      service.getDistanceMatrix(
        {
          origins: [origin],
          destinations: [destination],
          travelMode: window.google.maps.TravelMode.DRIVING,
          unitSystem: window.google.maps.UnitSystem.METRIC,
        },
        (response, status) => {
          if (status === "OK") {
            const element = response.rows[0].elements[0];
            if (element.status === "OK") {
              resolve({
                distance: element.distance.value / 1000, // km
                duration: element.duration.value / 3600, // tunnit
                distanceText: element.distance.text,
                durationText: element.duration.text
              });
            } else {
              reject(new Error("Matkan laskenta epäonnistui: " + element.status));
            }
          } else {
            reject(new Error("Google API status: " + status));
          }
        }
      );
    });
  };

  // Haetaan bändin tiedot slugilla
  useEffect(() => {
    if (!bandSlug) return;
    axios
      .get(`https://spiderlegsproductions.com/api/get_band.php?slug=${bandSlug}`)
      .then((res) => {
        if (res.data.success && res.data.band) {
          const band = res.data.band;
          setBandData(band);
          if (band.available_sets) {
            const sets = band.available_sets.split(",").map((s) => s.trim());
            setAvailableSets(sets);
          }
          if (band.performance_regions) {
            const regionList = band.performance_regions.split(",").map((r) => r.trim().toLowerCase());
            setBandRegions(regionList);
          }
        }
      })
      .catch((err) => {
        console.error("Virhe haettaessa bändin tietoja:", err);
      });
  }, [bandSlug]);

  // Hinnan haku
  useEffect(() => {
    if (bandSlug && formData.gig_length && priceRegion) {
      axios
        .get("https://spiderlegsproductions.com/api/get_price.php", {
          params: {
            slug: bandSlug,
            region: priceRegion,
            duration: formData.gig_length
          }
        })
        .then((res) => {
          if (res.data.success) {
            setPrice(res.data.price);
          } else {
            setPrice(null);
            console.error("Hinta ei saatavilla:", res.data.error);
          }
        })
        .catch((err) => {
          console.error("Virhe hinnan haussa:", err);
          setPrice(null);
        });
    }
  }, [bandSlug, formData.gig_length, priceRegion]);

  useEffect(() => {
    // Nollaa lomakekentät kun lomaketyyppi vaihtuu
    setFormData({
      customer_name: "",
      customer_email: "",
      customer_phone: "",
      additional_info: "",
      gig_length: "",
    });
    setTapahtumapaikka("");
    setStarRating(0);
    setPrice(null);
    setPriceRegion("");
    setDistanceText("");
    setDurationText("");
    setTravelCost(null);
    setFormError(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  }, [formMode]);
  

  const capitalizeEachWord = (text) => {
    return text
      .split(/[\s-]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join("-");
  };

  // Google Maps Autocomplete: Kun paikka valitaan
  const onPlaceChanged = () => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (!place || !place.address_components) return;
    let region = "";
    place.address_components.forEach((component) => {
      if (component.types.includes("administrative_area_level_1")) {
        region = component.long_name.toLowerCase();
      }
    });
    if (region && bandRegions.length > 0) {
      const formattedRegion = capitalizeEachWord(region);
      const formattedBandRegions = bandRegions.map(capitalizeEachWord);
      if (!formattedBandRegions.includes(formattedRegion)) {
        toast.error(`Valitettavasti esiintyjä ei keikkaile tällä alueella. 😒`);
        setTapahtumapaikka("");
        setPriceRegion("");
        return;
      }
    }
    setPriceRegion(region || "");
    if (place.formatted_address) {
      setTapahtumapaikka(place.formatted_address);
    } else {
      setTapahtumapaikka(place.name || "");
    }
  };

  // YHTEINEN handleFormSubmit – Käyttää yhtä EmailJS-templatea
  const handleFormSubmit = async (e) => {

    if (!recaptchaValue) {
      toast.error("Vahvista reCAPTCHA ennen lähettämistä.");
      return;
    }

    e.preventDefault();
    console.log("📌 Lomaketyyppi (formMode):", formMode);
    console.log("Valittu päivä:", selectedDate);
    setFormError(null);
    console.log("🔧 TEMPLATE_ID:", EMAILJS_TEMPLATE_ID);

    // Jos kyseessä on "setit"-tila, varmistetaan päivämäärän kelpoisuus
    const selectedDateObj = selectedDate?.iso ? new Date(selectedDate.iso) : null;
    const todayMidnight = new Date(new Date().toDateString());
    if (formMode === "setit") {
      if (!selectedDateObj || isNaN(selectedDateObj.getTime())) {
        toast.error("Virheellinen päivämäärä.");
        return;
      }
      if (selectedDateObj < todayMidnight) {
        toast.error("Valitse nykyinen tai tuleva päivä.");
        return;
      }
    }
    let formattedDate = null;
    if (formMode === "setit" && selectedDate?.iso) {
      formattedDate = new Date(selectedDate.iso).toISOString().split("T")[0];
    }

    try {
      // Laske tähdet, etäisyys, matka-aika jne.
      const stars_visual = "★".repeat(starRating) + "☆".repeat(5 - starRating);
      let distance = null;
      let travelTime = null;
      let durationTextFormatted = "–";
      if (formMode === "setit" && bandData?.band_address && tapahtumapaikka) {
        const result = await getDistanceAndDuration(bandData.band_address, tapahtumapaikka);
        distance = result.distance;
        travelTime = result.duration;
        const getDurationText = (seconds) => {
          const mins = Math.round(seconds / 60);
          const hours = Math.floor(mins / 60);
          const remainingMins = mins % 60;
          return `${hours > 0 ? hours + " tuntia " : ""}${remainingMins > 0 ? remainingMins + " min" : ""}`.trim();
        };
        durationTextFormatted = getDurationText(travelTime * 3600);
      }
      const kmPrice = 0.5;
      const cost = distance ? distance * 2 * kmPrice : 0;
      setTravelCost(cost);

      // Jos kyseessä on varaus ("setit"), tallennetaan ensin tietokantaan
      if (formMode === "setit") {
        const response = await fetch("https://spiderlegsproductions.com/api/save_booking.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            band_id: bandData?.id || null,
            customer_name: formData.customer_name,
            customer_email: formData.customer_email,
            customer_phone: formData.customer_phone,
            event_date: selectedDate?.iso,
            event_location: tapahtumapaikka,
            user_message: formData.additional_info,
            price: price !== null ? `${price}` : "0",
          }),
        });
        const rawText = await response.text();
        console.log("📦 Response TEXT:", rawText);
        let result;
        try {
          result = JSON.parse(rawText);
        } catch (err) {
          console.error("❌ JSON-virhe:", err.message);
          toast.error("Palvelin ei palauttanut kelvollista JSON-vastausta.");
          return;
        }
        if (!response.ok || !result.success) {
          throw new Error(result.error || "Tietokantavirhe");
        }
      }

      // Rakennetaan sähköpostiin lähetettävät HTML-lohkot
      const event_info_block = formMode === "setit" ? `
        <h3 style="color: #ff4e50; font-size: 18px;">📅 Tapahtuman tiedot</h3>
        <table style="width: 100%; border-collapse: collapse; font-size: 15px; margin-bottom: 20px;">
          <tbody>
            <tr><td><strong>Ajankohta:</strong></td><td>${formattedDate}</td></tr>
            <tr><td><strong>Tapahtumapaikka:</strong></td><td>${tapahtumapaikka}</td></tr>
            <tr><td><strong>Valittu pituus:</strong></td><td>${formData.gig_length || "–"}</td></tr>
            <tr><td><strong>Bändin antama hinta:</strong></td><td>${price !== null ? price + " €" : "Ei määritelty"}</td></tr>
            <tr><td><strong>Esiintyjän matka kohteeseen:</strong></td><td>${distance !== null ? distance.toFixed(1) + " km" : "–"}</td></tr>
            <tr><td><strong>Arvioitu matka-aika:</strong></td><td>${durationTextFormatted || "–"}</td></tr>
            <tr><td><strong>Edestakainen matka:</strong></td><td>${distance !== null ? (distance * 2).toFixed(1) + " km" : "–"}</td></tr>
          </tbody>
        </table>
      ` : "";
      const contact_info_block = formMode === "setit" ? `
        <h3 style="color: #7b1fa2; font-size: 18px;">🎸 Esiintyjän yhteystiedot</h3>
        <table style="width: 100%; border-collapse: collapse; font-size: 15px; margin-bottom: 20px;">
          <tbody>
            <tr><td><strong>Bändin nimi:</strong></td><td>${bandData?.band_name || bandSlug}</td></tr>
            <tr><td><strong>Osoite:</strong></td><td>${bandData?.band_address || "Ei ilmoitettu"}</td></tr>
            <tr><td><strong>Sähköposti:</strong></td><td>${bandData?.band_email || "Ei ilmoitettu"}</td></tr>
            <tr><td><strong>Puhelin:</strong></td><td>${bandData?.band_phone || "Ei ilmoitettu"}</td></tr>
          </tbody>
        </table>
      ` : "";
      const message_block = (formMode === "viesti" || formMode === "palaute") ? `
        <h3 style="color: #f9a825; font-size: 18px;">${formMode === "viesti" ? "📨 Viesti" : "📝 Palaute"}</h3>
        <div style="border: 1px solid #eee; padding: 14px; border-radius: 8px; background-color: #fefbe9;">
          ${formData.additional_info || ""}
        </div>
      ` : "";
      const rating_block = formMode === "palaute" ? `
        <h3 style="color: #00c853; font-size: 18px; margin-top: 20px;">⭐ Arvio</h3>
        <p style="font-size: 15px;"><strong>Annettu:</strong> ${stars_visual} (${starRating} / 5)</p>
      ` : "";

// Yhdistetty bookingData – rakenne riippuen lomaketyypistä
let bookingData = {};

if (formMode === "palaute") {
  bookingData = {
    form_type: "Palaute",
    band_name: bandData?.band_name || bandSlug || "–",
    band_address: bandData?.band_address || "–",
    band_email: bandData?.band_email || "–",
    band_phone: bandData?.band_phone || "–",
    from_name: formData.customer_name || "–",
    user_name: formData.customer_name || "–",
    user_email: formData.customer_email || "–",
    user_phone: "", // palautteessa ei tarvita mutta template voi odottaa
    event_date: "", // ei palaute-viestissä
    event_location: "",
    gig_length: "",
    price: "",
    distance: "",
    duration_text: "",
    distance_total: "",
    user_message: formData.additional_info || "",
    stars_visual,
    star_rating: starRating,
    show_rating: true,
    is_feedback: true,
    is_message: false,
    is_quote: false,
    event_info_block: "",
    contact_info_block: "",
    message_block: `
      <h3 style="color: #f9a825; font-size: 18px;">📝 Palaute</h3>
      <div style="border: 1px solid #eee; padding: 14px; border-radius: 8px; background-color: #fefbe9;">
        ${formData.additional_info || ""}
      </div>
    `,
    rating_block: `
      <h3 style="color: #00c853; font-size: 18px; margin-top: 20px;">⭐ Arvio</h3>
      <p style="font-size: 15px;"><strong>Annettu:</strong> ${stars_visual} (${starRating} / 5)</p>
    `,
  };

  // 📝 TALLENNETAAN PALAUTE TIETOKANTAAN
  try {
    const feedbackResponse = await fetch("https://spiderlegsproductions.com/api/save_feedback.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        band_id: bandData?.id || null,
        user_name: formData.customer_name,
        user_email: formData.customer_email,
        rating: starRating,
        feedback: formData.additional_info,
      }),
    });

    const feedbackResult = await feedbackResponse.json();
    if (!feedbackResponse.ok || !feedbackResult.success) {
      console.error("❌ Palaute tallennus epäonnistui:", feedbackResult.error);
      toast.error("Palauteen tallennus epäonnistui. Yritä uudelleen.");
      return;
    } else {
      console.log("✅ Palaute tallennettu:", feedbackResult);
    }
  } catch (error) {
    console.error("❌ Virhe palautteen lähetyksessä:", error);
    toast.error("Palauteen tallennuksessa tapahtui virhe.");
    return;
  }

} else if (formMode === "viesti") {
  bookingData = {
    form_type: "Viesti",
    band_name: bandData?.band_name || bandSlug || "–",
    band_address: bandData?.band_address || "Ei ilmoitettu",
    band_email: bandData?.band_email || "Ei ilmoitettu",
    band_phone: bandData?.band_phone || "Ei ilmoitettu",
    from_name: formData.customer_name || "–",
    user_name: formData.customer_name || "–",
    user_email: formData.customer_email || "–",
    user_phone: formData.customer_phone || "–",
    user_message: formData.additional_info || "",
    stars_visual: "",
    star_rating: null,
    show_rating: false,
    is_feedback: false,
    is_message: true,
    is_quote: false,
  };

} else if (formMode === "setit") {
  bookingData = {
    form_type: "Tarjouspyyntö",
    is_feedback: false,
    is_message: false,
    is_quote: true,
    band_name: bandData?.band_name || bandSlug || "–",
    band_address: bandData?.band_address || "Ei ilmoitettu",
    band_email: bandData?.band_email || "Ei ilmoitettu",
    band_phone: bandData?.band_phone || "Ei ilmoitettu",
    from_name: formData.customer_name || "–",
    user_name: formData.customer_name || "–",
    user_email: formData.customer_email || "–",
    user_phone: formData.customer_phone || "–",
    event_date: formattedDate || "–",
    event_location: tapahtumapaikka || "–",
    gig_length: formData.gig_length || "–",
    price: price !== null ? `${price} €` : "Hinta ei määritelty",
    distance: distance !== null ? distance.toFixed(1) : "–",
    duration_text: durationTextFormatted,
    distance_total: distance !== null ? (distance * 2).toFixed(1) + " km" : "–",
    user_message: "", // Tarjouspyynnössä oma viesti ei käytetä
    stars_visual: "",
    star_rating: null,
    show_rating: false,
    event_info_block,
    message_block,
    rating_block,
    contact_info_block,
  };
} else {
  console.error("❌ Tuntematon formMode:", formMode);
  toast.error("Tuntematon lomaketyyppi. Lähetys keskeytettiin.");
  return;
}

const selectedTemplateId = EMAILJS_TEMPLATE_ID?.trim();

// 💌 Lähetetään sähköposti
await emailjs.send(
  EMAILJS_SERVICE_ID?.trim(),
  selectedTemplateId,
  bookingData,
  EMAILJS_USER_ID?.trim()
);


      // Asiakkaalle lähetettävä viesti – vain "setit"-tilassa
if (formMode === "setit") {
  try {
    await emailjs.send(
      EMAILJS_SERVICE_ID?.trim(),
      EMAILJS_TEMPLATE_ID_CUSTOMER?.trim(),
      {
        user_name: formData.customer_name || "–",
        user_email: formData.customer_email || "–",
        event_date: formattedDate || "–",
        event_location: tapahtumapaikka || "–",
        gig_length: formData.gig_length || "–",
        price: price !== null ? `${price} €` : "Hinta ei määritelty",
        band_name: bandData?.band_name || bandSlug || "–"
      },
      EMAILJS_USER_ID?.trim()
    );
  } catch (err) {
    console.error("❌ Virhe asiakkaan viestissä:", err);
  }
}

      // Näytetään ilmoitus käyttäjälle
      const toastTexts = {
        setit: ["Tarjouspyyntösi on lähetetty!", "Saat pian sähköpostiisi tarjouksen ja ohjeet sen vahvistamiseen."],
        viesti: ["Viestisi lähetetty!", "Se on toimitettu esiintyjälle. Saat vastauksen pian."],
        palaute: ["Kiitos palautteestasi!", "Arvostamme sitä todella. Se auttaa esiintyjää saamaan näkyvyyttä!"],
      };
      const [toastTitle, toastText] = toastTexts[formMode] || ["Viesti lähetetty!", "Saat pian sähköpostiisi yhteenvedon."];
      toast(({ closeToast }) => (
        <div className="w-full max-w-xl mx-auto px-6 flex flex-col items-center justify-center text-center text-white text-lg sm:text-xl p-6 rounded-2xl shadow-2xl bg-gradient-to-br from-pink-500 via-purple-600 to-blue-500">
          <p className="font-light font-customFont1 text-lg mb-2">{toastTitle}</p>
          <motion.p className="mb-4" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            {toastText}
          </motion.p>
          <button onClick={closeToast} className="px-4 py-2 bg-white font-customFont1 text-black font-semibold rounded-full hover:bg-gray-200 transition">
            Sulje
          </button>
        </div>
      ), {
        position: "bottom-right",
        autoClose: 20000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        className: "!fixed bottom-0 right-0 z-[9999] w-full px-4",
        bodyClassName: "w-full flex justify-center",
      });
      
      // Tyhjennetään lomaketiedot
      setFormData({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        additional_info: "",
        gig_length: "",
      });
      setTapahtumapaikka("");
      setStarRating(0);
      setPrice(null);
      setPriceRegion("");
      setDistanceText("");
      setDurationText("");
      setTravelCost(null);

      setRecaptchaValue(null);
if (recaptchaRef.current) {
  recaptchaRef.current.reset();
}
  
    } catch (err) {
      console.error("❌ Virhe:", err);
      toast.error("Jotain meni pieleen: " + err.message);
    }
  };

  
  return (
      <div>
        {/* Lomakkeen käyttötarkoituksen valinta */}
        {/* ... Lomakkeen muut osiot ... */}
        <div className="max-w-md w-full mx-auto px-2 sm:px-4 lg:px-8">
          <div className="w-full max-w-md">
            <form onSubmit={handleFormSubmit} className="space-y-4 dark:bg-violet bg-primary dark:bg-opacity-75 bg-opacity-75 rounded-lg p-3 sm:p-5 mb-4">
              {/* setit-tila */}
              {formError && (
      <div className="bg-red-100 text-red-700 px-4 py-2 rounded-lg mb-4 text-center">
        {formError}
      </div>
    )}
              {formMode === "setit" && (
                <div>
                  {availableSets.length > 0 && (
                    <>
                      <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
                        <label htmlFor="gig_length" className="block font-customFont1 text-shadow-lg text-center text-darkest dark:text-primary xxxsm:text-sm xxsm:text-sm xsm:text-sm text-lg font-bold mb-2">
                          Valitse esiintymisen pituus:
                        </label>
                      </motion.div>
                      <div className="flex flex-wrap justify-center gap-4">
                        {availableSets.map((set, index) => {
                          const radioId = `gig_length_${index}`;
                          return (
                            <label key={index} htmlFor={radioId} className="flex items-center space-x-2 cursor-pointer">
                              <motion.input
                                id={radioId}
                                type="radio"
                                name="gig_length"
                                value={set}
                                checked={formData.gig_length === set}
                                onChange={(e) => setFormData({ ...formData, gig_length: e.target.value })}
                                className="form-radio text-blue-500 focus:ring-blue-300"
                                required
                                whileTap={{ scale: 1.2, transition: { duration: 0.15, type: "spring", stiffness: 300 } }}
                              />
                              <span className="font-customFont1 text-shadow-lg text-left text-darkest dark:text-primary xxxsm:text-sm xxsm:text-sm xsm:text-sm text-lg font-light">
                                {set}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </>
                  )}
  
                  <motion.div className="mt-4">
                    <label htmlFor="event_location" className="block font-customFont1 text-shadow-lg text-left text-primary dark:text-primary xxxsm:text-sm xxsm:text-sm xsm:text-sm text-lg font-bold mb-2">
                      Tapahtumapaikka:
                    </label>
                    <Autocomplete onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)} onPlaceChanged={onPlaceChanged} options={{ componentRestrictions: { country: "fi" } }}>
                      <div className="relative">
                        <motion.input
                          id="event_location"
                          type="text"
                          placeholder="Anna tapahtumapaikka.."
                          value={tapahtumapaikka}
                          onChange={(e) => setTapahtumapaikka(e.target.value)}
                          required
                          className="w-full p-3 rounded-full bg-white dark:bg-darkest border-none focus:outline-none text-lg font-customFont1 pl-5"
                          whileFocus={{ scale: 1.05, transition: { duration: 0.2, type: "spring", stiffness: 300 } }}
                        />
                      </div>
                    </Autocomplete>
                  </motion.div>
                  {/* {price !== null && (
                    <>
                      <p className="font-customFont1 text-center text-darkest dark:text-primary text-xl font-bold mt-2">
                        Hinta: {price} €
                      </p>
                      {distanceText && travelCost && durationText && (
                        <div className="mt-3 text-center font-customFont1 text-darkest dark:text-primary text-lg font-medium space-y-1">
                          <p><strong>Matka bändiltä kohteeseen:</strong> {distanceText}</p>
                          <p><strong>Arvioitu matka-aika (yksi suunta):</strong> {durationText}</p>
                          <p><strong>Matkaa yhteensä:</strong> {(parseFloat(distanceText) * 2).toFixed(1)} km</p>
                        </div>
                      )}
                    </>
                  )} */}
                </div>
              )}
  
              {formMode === "viesti" && (
                <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.5 }}>
                  <label htmlFor="additional_info_viesti" className="block font-customFont1 dark:text-shadow-lg text-left text-darkest dark:text-primary xxxsm:text-sm xxsm:text-sm xsm:text-sm text-lg font-bold mb-2">
                    Viesti tai kysymyksesi
                  </label>
                  <motion.textarea
                    id="additional_info_viesti"
                    name="additional_info"
                    rows="4"
                    placeholder="Kirjoita viestisi..."
                    className="w-full p-3 rounded-xl bg-white dark:bg-darkest border-none focus:outline-none text-lg font-customFont1 pl-5"
                    value={formData.additional_info}
                    onChange={(e) => setFormData({ ...formData, additional_info: e.target.value })}
                    whileFocus={{ scale: 1.05, transition: { duration: 0.2, type: "spring", stiffness: 300 } }}
                  />
                </motion.div>
              )}
  
              {formMode === "palaute" && (
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <label
                  htmlFor="additional_info_palaute"
                  className="block font-customFont1 dark:text-shadow-lg text-left text-darkest dark:text-primary xxxsm:text-sm xxsm:text-sm xsm:text-sm text-lg font-bold mb-2"
                >
                  Jätä palautetta:
                </label>

                <motion.textarea
                  id="additional_info_palaute"
                  name="additional_info"
                  rows="4"
                  placeholder="Kirjoita palautteesi..."
                  className="w-full p-3 rounded-xl bg-white dark:bg-darkest border-none focus:outline-none text-lg font-customFont1 pl-5"
                  value={formData.additional_info}
                  onChange={(e) =>
                    setFormData({ ...formData, additional_info: e.target.value })
                  }
                  whileFocus={{
                    scale: 1.05,
                    transition: { duration: 0.2, type: "spring", stiffness: 300 },
                  }}
                />

                <motion.p
                  className={`font-customFont1 text-left xxxsm:text-sm xxsm:text-sm xsm:text-sm text-lg font-bold mb-2 mt-4 ${
                    isPremium ? "text-darkest dark:text-primary" : "text-gray-500"
                  }`}
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  Anna arvosana:
                </motion.p>

                <StarRating
                  rating={starRating}
                  onRatingChange={(newRating) => setStarRating(newRating)}
                  disabled={!isPremium}
                />

                {/* 🔥 Tämä näkyy vain jos EI ole premium */}
                {!isPremium && (
                  <div className="mt-2 xxxsm:text-sm xxsm:text-sm xsm:text-sm text-lg dark:text-red-500 font-customFont1 flex items-center gap-2">
                    <span>⭐</span>
                    <span>
                      Tähtiarviointi on käytössä vain esiintyjille, joilla on Premium-jäsenyys.
                    </span>
                  </div>
                )}
              </motion.div>
            )}

              <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
                <label htmlFor="customer_name" className="block font-customFont1 text-left text-darkest dark:text-primary xxxsm:text-sm xxsm:text-sm xsm:text-sm text-lg font-bold mb-2">
                  Nimi:
                </label>
                <motion.input
                  id="customer_name"
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  required
                  placeholder="Nimesi.."
                  className="w-full p-3 rounded-full bg-white dark:bg-darkest border-none focus:outline-none text-lg font-customFont1 pl-5"
                  whileFocus={{ scale: 1.05, transition: { duration: 0.2, type: "spring", stiffness: 300 } }}
                />
              </motion.div>
  
              <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
                <label htmlFor="customer_email" className="block font-customFont1 text-left text-darkest dark:text-primary xxxsm:text-sm xxsm:text-sm xsm:text-sm text-lg font-bold mb-2">
                  Sähköposti:
                </label>
                <motion.input
                  id="customer_email"
                  type="email"
                  name="customer_email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  required
                  placeholder="Sähköpostiosoitteesi.."
                  className="w-full p-3 rounded-full bg-white dark:bg-darkest border-none focus:outline-none text-lg font-customFont1 pl-5"
                  whileFocus={{ scale: 1.05, transition: { duration: 0.2, type: "spring", stiffness: 300 } }}
                />
              </motion.div>
  
              {formMode !== "palaute" && (
                <motion.div>
                  <label htmlFor="customer_phone" className="block font-customFont1 text-left text-darkest dark:text-primary xxxsm:text-sm xxsm:text-sm xsm:text-sm text-lg font-bold mb-2">
                    Puhelinnumero:
                  </label>
                  <motion.input
                    id="customer_phone"
                    type="text"
                    name="customer_phone"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                    placeholder="Puhelinnumerosi.."
                    className="w-full p-3 rounded-full bg-white dark:bg-darkest border-none focus:outline-none text-lg font-customFont1 pl-5"
                    required
                    whileFocus={{ scale: 1.05, transition: { duration: 0.2, type: "spring", stiffness: 300 } }}
                  />
                </motion.div>
              )}

              <RecaptchaField
                siteKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={(value) => setRecaptchaValue(value)}
                innerRef={recaptchaRef}
              />

              <div className="w-full flex justify-center">
                <ResponsiveButton type="submit" className="w-full max-w-xs">
                  Lähetä
                </ResponsiveButton>
              </div>
            </form>
          </div>
        </div>
      </div>
  );
};
  
BookingForm.propTypes = {
  bandSlug: PropTypes.string.isRequired,
  selectedDate: PropTypes.shape({
    iso: PropTypes.string.isRequired,
    formatted: PropTypes.string.isRequired,
  }),
  formMode: PropTypes.string,
  isPremium: PropTypes.bool, // ✅ Lisätty prop: onko premium-jäsen
};

export default BookingForm;
